import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IAddressDTO } from '@common-frontend-submodule/interfaces/address/iaddress.interface';
import { IAddressCreationDTO } from '@common-frontend-submodule/interfaces/address/iaddress-creation.interface';
import { IAddressUpdateDTO } from '@common-frontend-submodule/interfaces/address/iaddress-update.interface';
import { IGetAddressesRequestDTO } from '@common-frontend-submodule/interfaces/address/iget-addresses-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { AddressRoutes } from '@common-frontend-submodule/enums/routes/address-routes.enum';

const apiClient = ApiClient.getInstance()

interface AddressContextProps {
  getById: (addressId: string) => Promise<IApiResponseGenericDTO<IAddressDTO>>;
  allClientAddresses: (id: string) => Promise<IApiResponseGenericDTO<IAddressDTO[]>>;
  filter: (model: IGetAddressesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IAddressDTO>>>;
  create: (model: IAddressCreationDTO) => Promise<IApiResponseGenericDTO<IAddressDTO>>;
  update: (addressId: string, model: IAddressUpdateDTO) => Promise<IApiResponseGenericDTO<IAddressDTO>>;
  delete: (addressId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const AddressContext = createContext<AddressContextProps | undefined>(undefined);

export const AddressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (addressId: string): Promise<IApiResponseGenericDTO<IAddressDTO>> => {
    return await apiClient.getRequest<IAddressDTO>(`${AddressRoutes.BasePrefix}/${addressId}`);
  };

  const allClientAddresses = async (id: string): Promise<IApiResponseGenericDTO<IAddressDTO[]>> => {
    return await apiClient.getRequest<IAddressDTO[]>(`${AddressRoutes.BasePrefix}/all/${id}`);
  };

  const filter = async (model: IGetAddressesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IAddressDTO>>> => {
    return await apiClient.postRequest<IGetAddressesRequestDTO, IPaginatedResultDTO<IAddressDTO>>(
      `${AddressRoutes.BasePrefix}/${AddressRoutes.List}`,
      model
    );
  };

  const create = async (model: IAddressCreationDTO): Promise<IApiResponseGenericDTO<IAddressDTO>> => {
    return await apiClient.postRequest<IAddressCreationDTO, IAddressDTO>(AddressRoutes.BasePrefix, model);
  };

  const update = async (addressId: string, model: IAddressUpdateDTO): Promise<IApiResponseGenericDTO<IAddressDTO>> => {
    return await apiClient.putRequest<IAddressUpdateDTO, IAddressDTO>(`${AddressRoutes.BasePrefix}/${addressId}`, model);
  };

  const deleteAddress = async (addressId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${AddressRoutes.BasePrefix}/${addressId}`);
  };

  return (
    <AddressContext.Provider value={{ getById, allClientAddresses, filter, create, update, delete: deleteAddress }}>
      {children}
    </AddressContext.Provider>
  );
};

export const useAddressStore = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddressStore must be used within a AddressProvider');
  }
  return context;
};
